import type { Edge, Node } from 'reactflow';
import type { JSONContent } from '@tiptap/react';
import { MEDIA_BLOCK_TYPES } from '../nodes/flowEditorBlockNodeTypes';

// -- Action classification ------------------------------------------------------
// The undo recorder is a single chokepoint: it sees only that the graph changed
// from one snapshot to the next, not which user action caused it. The inspector
// needs to name each entry in recognisable terms ("option reorder", "delete
// node", "message edit") so a missing entry is obvious at a glance. Rather than
// have every action tag itself (the fragile, forgettable wiring we deliberately
// removed), we INFER the action by diffing before -> after. Inference shares the
// chokepoint's "can't be forgotten" property: a new edit path is classified the
// moment it changes the graph, with no extra wiring.

export type FlowGraph = { nodes: Node[]; edges: Edge[] };

export type FlowActionKind =
  | 'add-node'
  | 'delete-node'
  | 'duplicate-node'
  | 'rename'
  | 'option-add'
  | 'option-remove'
  | 'option-reorder'
  | 'option-edit'
  | 'persona-change'
  | 'unset-persona'
  | 'message-add'
  | 'message-delete'
  | 'message-move'
  | 'media-insert'
  | 'media-remove'
  | 'link-edit'
  | 'message-edit'
  | 'node-move'
  | 'connect'
  | 'disconnect'
  | 'edit'; // fallback: a real change we couldn't pin to a specific kind

export type FlowAction = {
  kind: FlowActionKind;
  label: string; // recognisable name, e.g. "option reorder"
  summary: string; // short detail, e.g. node label or "+4 chars"
};

const LABELS: Record<FlowActionKind, string> = {
  'add-node': 'add node',
  'delete-node': 'delete node',
  'duplicate-node': 'duplicate node',
  rename: 'rename',
  'option-add': 'option add',
  'option-remove': 'option remove',
  'option-reorder': 'option reorder',
  'option-edit': 'option edit',
  'persona-change': 'persona change',
  'unset-persona': 'unset persona',
  'message-add': 'add message',
  'message-delete': 'delete message',
  'message-move': 'message move',
  'media-insert': 'media insert',
  'media-remove': 'media remove',
  'link-edit': 'link',
  'message-edit': 'message edit',
  'node-move': 'node move',
  connect: 'connect',
  disconnect: 'disconnect',
  edit: 'edit',
};

function nodeName(node: Node | undefined): string {
  const label = node?.data?.label;
  return typeof label === 'string' && label.length > 0 ? `"${label}"` : (node?.id ?? '');
}

// Walk a tiptap doc and collect the top-level block personaIds in order, plus a
// flat text length and a count of media/file nodes, so a doc diff can tell a
// persona change from a reorder from a media insert from plain typing.

type DocShape = {
  blockPersonas: (string | null)[];
  // A fingerprint per top-level block (persona + its text), so a pure reorder
  // (same fingerprints, new order) is distinguishable from a persona change
  // (same order, one fingerprint's persona differs).
  blockFingerprints: string[];
  textLength: number;
  mediaCount: number;
  // What kinds of media are present (gif / youtube / image / file), so a media
  // insert can name what was added rather than just "media".
  mediaKinds: string[];
  linkCount: number;
};

function summariseDoc(doc: JSONContent | undefined): DocShape {
  const blockPersonas: (string | null)[] = [];
  const blockFingerprints: string[] = [];
  let textLength = 0;
  let mediaCount = 0;
  const mediaKinds: string[] = [];
  let linkCount = 0;

  const blockText = (block: JSONContent): string => {
    let text = '';
    const walk = (node: JSONContent) => {
      if (typeof node.text === 'string') {
        text += node.text;
      }
      if (Array.isArray(node.content)) {
        node.content.forEach(walk);
      }
    };
    walk(block);
    return text;
  };

  const visit = (node: JSONContent, depth: number) => {
    if (depth === 1) {
      const personaId = node.attrs?.personaId;
      const persona = typeof personaId === 'string' ? personaId : null;
      blockPersonas.push(persona);
      blockFingerprints.push(`${persona ?? ''}::${blockText(node)}`);
    }
    if (typeof node.text === 'string') {
      textLength += node.text.length;
    }
    if (node.type && MEDIA_BLOCK_TYPES.has(node.type)) {
      mediaCount += 1;
      // resizableMedia carries a `media-type` (gif/youtube/img/video); fall back
      // to the node type name for files/uploads.
      const kind = node.attrs?.['media-type'];
      mediaKinds.push(typeof kind === 'string' ? kind : (node.type ?? 'media'));
    }
    // Links are a mark on text, not a node.
    if (Array.isArray(node.marks) && node.marks.some((m) => m?.type === 'link')) {
      linkCount += 1;
    }
    if (Array.isArray(node.content)) {
      for (const child of node.content) {
        visit(child, depth + 1);
      }
    }
  };

  if (doc) {
    visit(doc, 0);
  }
  return { blockPersonas, blockFingerprints, textLength, mediaCount, mediaKinds, linkCount };
}

function sameMembership(a: string[], b: string[]): boolean {
  if (a.length !== b.length) {
    return false;
  }
  const sa = [...a].sort();
  const sb = [...b].sort();
  return sa.every((v, i) => v === sb[i]);
}

// Classify the change to a single node's data (both versions exist).
function classifyNodeDataChange(before: Node, after: Node): FlowAction | null {
  const b = before.data ?? {};
  const a = after.data ?? {};
  const name = nodeName(after);

  if (b.label !== a.label) {
    return { kind: 'rename', label: LABELS.rename, summary: `${nodeName(before)} -> ${name}` };
  }

  const bOpts: { id: string; title: string }[] = b.options ?? [];
  const aOpts: { id: string; title: string }[] = a.options ?? [];
  if (bOpts.length !== aOpts.length) {
    const kind = aOpts.length > bOpts.length ? 'option-add' : 'option-remove';
    return { kind, label: LABELS[kind], summary: `${name} ${bOpts.length}->${aOpts.length}` };
  }
  if (bOpts.length > 0) {
    const bIds = bOpts.map((o) => o.id);
    const aIds = aOpts.map((o) => o.id);
    const orderChanged = bIds.some((id, i) => id !== aIds[i]);
    if (orderChanged && sameMembership(bIds, aIds)) {
      return { kind: 'option-reorder', label: LABELS['option-reorder'], summary: name };
    }
    const titleChanged = aOpts.some(
      (o, i) => bOpts[i] && o.id === bOpts[i].id && o.title !== bOpts[i].title,
    );
    if (titleChanged) {
      return { kind: 'option-edit', label: LABELS['option-edit'], summary: name };
    }
  }

  const bDoc = summariseDoc(b.tiptap);
  const aDoc = summariseDoc(a.tiptap);

  const sameBlockCount = bDoc.blockPersonas.length === aDoc.blockPersonas.length;

  // A pure reorder: the same set of blocks (matched by persona+text fingerprint)
  // in a new order. Check before persona-change, because swapping two blocks also
  // changes the persona seen at each position and would otherwise read as a
  // persona change.
  if (
    sameBlockCount &&
    bDoc.blockFingerprints.join('|') !== aDoc.blockFingerprints.join('|') &&
    sameMembership(bDoc.blockFingerprints, aDoc.blockFingerprints)
  ) {
    return { kind: 'message-move', label: LABELS['message-move'], summary: name };
  }

  // Persona on one of the blocks changed (same number of blocks, same order).
  if (sameBlockCount && bDoc.blockPersonas.some((p, i) => p !== aDoc.blockPersonas[i])) {
    // Unset rejoins the previous run: a block's persona became the one above it.
    const idx = bDoc.blockPersonas.findIndex((p, i) => p !== aDoc.blockPersonas[i]);
    const becamePrevious = idx > 0 && aDoc.blockPersonas[idx] === aDoc.blockPersonas[idx - 1];
    const kind = becamePrevious ? 'unset-persona' : 'persona-change';
    return { kind, label: LABELS[kind], summary: name };
  }

  // Media added or removed, named by what it is (gif / youtube / image / file).
  // Checked before message add/delete because inserting media also changes the
  // block count, but "media insert" is the more meaningful label.
  if (aDoc.mediaCount !== bDoc.mediaCount) {
    const added = aDoc.mediaCount > bDoc.mediaCount;
    const kind = added ? 'media-insert' : 'media-remove';
    // The kinds that changed between before and after (best-effort label).
    const changedKinds = added
      ? aDoc.mediaKinds.filter((k, i) => k !== bDoc.mediaKinds[i])
      : bDoc.mediaKinds.filter((k, i) => k !== aDoc.mediaKinds[i]);
    const what = changedKinds[0] ?? aDoc.mediaKinds.at(-1) ?? 'media';
    return { kind, label: LABELS[kind], summary: `${name} ${what}` };
  }

  // A link was added or removed on text (a mark, not a node, so it can leave the
  // char count unchanged — which is why it used to read as "message edit +0").
  if (aDoc.linkCount !== bDoc.linkCount) {
    return { kind: 'link-edit', label: LABELS['link-edit'], summary: name };
  }

  // A message bubble was added or removed (block count changed, not explained by
  // media above). Deleting a bubble used to read as "message edit -N chars";
  // call it what it is.
  if (!sameBlockCount) {
    const kind =
      aDoc.blockPersonas.length > bDoc.blockPersonas.length ? 'message-add' : 'message-delete';
    return { kind, label: LABELS[kind], summary: name };
  }

  if (bDoc.textLength !== aDoc.textLength) {
    const delta = aDoc.textLength - bDoc.textLength;
    const sign = delta >= 0 ? '+' : '';
    return {
      kind: 'message-edit',
      label: LABELS['message-edit'],
      summary: `${name} ${sign}${delta} chars`,
    };
  }

  // Other data fields (reply, quizId, pickedOption) changed.
  return { kind: 'edit', label: LABELS.edit, summary: name };
}

/**
 * Infer the user action that turned `before` into `after`. Returns null when the
 * two graphs carry the same content (a no-op the recorder should not record).
 * Designed for the undo inspector: the label is for human recognition, not
 * persistence, so a best-effort guess on an ambiguous diff is fine.
 */
export function classifyFlowChange(before: FlowGraph, after: FlowGraph): FlowAction | null {
  const beforeNodes = new Map(before.nodes.map((n) => [n.id, n]));
  const afterNodes = new Map(after.nodes.map((n) => [n.id, n]));

  // Node count changed: add / delete / duplicate.
  if (afterNodes.size > beforeNodes.size) {
    const added = after.nodes.filter((n) => !beforeNodes.has(n.id));
    const first = added[0];
    // A duplicate clones an existing node's data (same label/options/tiptap).
    const isDuplicate = added.some((n) =>
      before.nodes.some((o) => JSON.stringify(o.data) === JSON.stringify(n.data)),
    );
    const kind = isDuplicate ? 'duplicate-node' : 'add-node';
    return { kind, label: LABELS[kind], summary: nodeName(first) };
  }
  if (afterNodes.size < beforeNodes.size) {
    const removed = before.nodes.filter((n) => !afterNodes.has(n.id));
    return { kind: 'delete-node', label: LABELS['delete-node'], summary: nodeName(removed[0]) };
  }

  // Edge count changed: connect / disconnect.
  if (after.edges.length !== before.edges.length) {
    const kind = after.edges.length > before.edges.length ? 'connect' : 'disconnect';
    return {
      kind,
      label: LABELS[kind],
      summary: `${before.edges.length}->${after.edges.length} edges`,
    };
  }

  // Same membership: find the node whose data or position changed.
  for (const [id, afterNode] of afterNodes) {
    const beforeNode = beforeNodes.get(id);
    if (!beforeNode) {
      continue;
    }
    const dataChanged = JSON.stringify(beforeNode.data) !== JSON.stringify(afterNode.data);
    if (dataChanged) {
      const action = classifyNodeDataChange(beforeNode, afterNode);
      if (action) {
        return action;
      }
    }
  }

  // No data change anywhere: a pure position move (drag).
  for (const [id, afterNode] of afterNodes) {
    const beforeNode = beforeNodes.get(id);
    if (!beforeNode) {
      continue;
    }
    if (
      beforeNode.position.x !== afterNode.position.x ||
      beforeNode.position.y !== afterNode.position.y
    ) {
      return { kind: 'node-move', label: LABELS['node-move'], summary: nodeName(afterNode) };
    }
  }

  return null;
}
